import { useEffect, useState } from 'react'
import { Alert, Empty, Flex, Input, Skeleton, Tag, Typography, theme } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { formatMoneyValue } from '@/shared/lib/money'
import { SubscriberStateTag } from '@/shared/ui/StatusTag'
import { displayPhone } from '@/shared/lib/phoneMask'
import { cashdeskKeys } from './cashdeskKeys'
import { searchDirectory, type DirectoryRow } from './cashdeskApi'

/** Kassir yozayotganda har harfda so'rov ketmasin */
const DEBOUNCE_MS = 350

/**
 * Ikki belgidan qisqa so'rov yuborilmaydi.
 *
 * Bitta harf butun bazani qaytaradi va telefonda bu bir necha soniyalik
 * kutish demak — kassir esa hali yozib ham bo'lmagan.
 */
const MIN_QUERY = 2

/** Bir qo'lda ishlash uchun eng kichik barmoq nishoni */
const ROW_HEIGHT = 76

interface ResultRowProps {
  row: DirectoryRow
  onOpen: (row: DirectoryRow) => void
}

/**
 * Natija qatori — jadval EMAS, bosiladigan karta.
 *
 * ⚠️ `shared/ui/table/DataTable` bu yerda ATAYLAB ishlatilmadi. U
 * kompyuter uchun qurilgan: ustun sarlavhalari, zichlik va ustun
 * sozlamalari, sahifalash, eksport, gorizontal scroll. 390px kenglikda
 * beshta ustun bir-biriga yopishadi va kassir bosishi kerak bo'lgan
 * nishon bitta hujayra kengligiga tushadi. Bu yerda ekranda BIR
 * savolga javob bo'lishi kerak: "shu odammi?" — shuning uchun bitta
 * ustunli, baland va butunlay bosiladigan qator.
 */
function ResultRow({ row, onOpen }: ResultRowProps) {
  const { t } = useTranslation()
  const { token } = theme.useToken()

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(row)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onOpen(row)
      }}
      style={{
        minHeight: ROW_HEIGHT,
        padding: '10px 14px',
        borderRadius: token.borderRadiusLG,
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        cursor: 'pointer',
      }}
    >
      <Flex align="center" justify="space-between" gap={12}>
        <Typography.Text strong ellipsis style={{ fontSize: 16 }}>
          {row.login}
        </Typography.Text>
        <Typography.Text strong style={{ fontSize: 16, whiteSpace: 'nowrap' }}>
          {formatMoneyValue(row.balance)}
        </Typography.Text>
      </Flex>

      <Typography.Paragraph
        type="secondary"
        ellipsis={{ rows: 1 }}
        style={{ margin: '2px 0 6px', fontSize: 13 }}
      >
        {/* ⛔ QIDIRUV QATORIDA RAQAM NIQOBLANGAN va bu yerda OCHISH
            TUGMASI YO'Q: qator BOSILADIGAN (abonentni tanlaydi) va
            uning ichidagi tugma o'sha bosishga to'qnashardi — kassir
            raqamni ochmoqchi bo'lib abonentni tanlab qo'yardi.
            To'liq raqam tanlangandan keyin KARTOCHKADA ochiladi
            (`SubscriberCardBody`).

            ⚠️ Qidiruvning O'ZI raqam bo'yicha ishlashda davom etadi:
            server to'liq qiymat bo'yicha qidiradi, niqob esa faqat
            chizishda. Ya'ni kassir raqamni terib abonentni topa oladi,
            lekin ekranda uni ko'rmaydi.

            ⚠️ `displayPhone`, xom `maskPhone` EMAS: server bir kun
            niqoblab yubora boshlasa, xom chaqiruv niqobni IKKINCHI
            marta qo'llardi. Formula bugun idempotent (o'lchandi), lekin
            unga TAYANILMAYDI — u o'zgarsa nuqson jimgina qaytardi. */}
        {[row.displayName, displayPhone(row.phone, false)].filter(Boolean).join(' · ')}
      </Typography.Paragraph>

      <Flex align="center" gap={6} wrap>
        <SubscriberStateTag state={row.state} />
        <Tag>{row.planName ?? t('common.dash')}</Tag>
      </Flex>
    </div>
  )
}

interface SubscriberSearchProps {
  onOpen: (row: DirectoryRow) => void
  /** Ochiq kartochkadagi abonent — yangilangan qatorni unga uzatish uchun */
  selectedId: string | null
  /**
   * ⚠️ OCHIQ KARTOCHKA YANGILANADI. Pul amalidan keyin kesh bekor
   * qilinadi va ro'yxat yangi balans bilan qaytadi — kartochka esa
   * chaqiruvchining holatida turadi va ESKI sonni ko'rsatib qolardi.
   * Ekrandagi eng katta raqam yolg'on bo'lishi mumkin bo'lgan yagona
   * joy shu, shuning uchun yangi qator yuqoriga qaytariladi.
   */
  onRefresh: (row: DirectoryRow) => void
}

/**
 * Qidiruv maydoni va natijalar.
 *
 * ⚠️ MAYDON EKRANNING ENG TEPASIDA va fokus avtomatik. Kassirning har
 * ishi shu maydondan boshlanadi — u kuniga o'nlab marta takrorlanadi va
 * har safar maydonni izlash sof yo'qotish.
 */
export function SubscriberSearch({ onOpen, selectedId, onRefresh }: SubscriberSearchProps) {
  const { t } = useTranslation()
  const [text, setText] = useState('')
  const [query, setQuery] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [text])

  const enabled = query.length >= MIN_QUERY
  const result = useQuery({
    queryKey: cashdeskKeys.search(query),
    queryFn: ({ signal }) => searchDirectory(query, signal),
    enabled,
    placeholderData: keepPreviousData,
  })

  const rows = result.data ?? []

  useEffect(() => {
    if (selectedId === null || !result.data) return
    const fresh = result.data.find((r) => r.id === selectedId)
    if (fresh) onRefresh(fresh)
  }, [result.data, selectedId, onRefresh])

  return (
    <Flex vertical gap={12}>
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        allowClear
        autoFocus
        size="large"
        prefix={<SearchOutlined />}
        placeholder={t('cashdesk.searchPlaceholder')}
        // ⚠️ Telefon klaviaturasi: `search` turi «Enter» o'rniga «Qidirish»
        // tugmasini chizadi va tugmani bosish maydonni yopadi — ya'ni
        // natijalar darhol ko'rinadi.
        inputMode="search"
        // ⚠️ `fontSize: 16` — bezak emas, XULQ. iOS Safari 16px dan
        // kichik matnli maydonga fokus tushganda sahifani AVTOMATIK
        // kattalashtiradi va orqaga qaytarmaydi: kassir har qidiruvdan
        // keyin ekranni ikki barmoq bilan kichraytirishga majbur bo'lardi.
        style={{ height: 52, fontSize: 16 }}
      />

      {!enabled ? (
        <Typography.Text type="secondary">{t('cashdesk.searchHint')}</Typography.Text>
      ) : null}

      {result.isError ? (
        <Alert type="error" showIcon message={translateError(result.error, t)} />
      ) : null}

      {enabled && result.isPending ? <Skeleton active paragraph={{ rows: 3 }} /> : null}

      {enabled && result.isSuccess && rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={4}>
              <Typography.Text strong>{t('cashdesk.notFoundTitle')}</Typography.Text>
              <Typography.Text type="secondary">{t('cashdesk.notFoundHint')}</Typography.Text>
            </Flex>
          }
        />
      ) : null}

      {rows.map((row) => (
        <ResultRow key={row.id} row={row} onOpen={onOpen} />
      ))}
    </Flex>
  )
}
